import {
  type TableDataCell as TableCellBlock,
  type TableHeaderCell as TableHeaderBlock,
  type TableRow as TableRowBlock,
  TextBlock,
} from "@prismicio/types-internal/lib/content"
import { Table as TTTable } from "@tiptap/extension-table"
import { TableCell as TTTableCell } from "@tiptap/extension-table-cell"
import { TableHeader as TTTableHeader } from "@tiptap/extension-table-header"
import { TableRow as TTTableRow } from "@tiptap/extension-table-row"
import type { Node as ProseMirrorNode, Schema } from "prosemirror-model"

import { NodeUtils, TableExtension } from "../../models"
import { ExtendedRichTextNodeType, type TableBlock } from "../../types"
import { Paragraph } from "../extensions"

function getNodeFromSchema(schema: Schema, name: string) {
  const node = schema.nodes[name]
  if (!node) {
    // This will only happen if RichTextField is not properly configured with
    // the necessary extensions (table, tableRow, tableHeader and tableCell),
    // so it should not be handling tables.
    throw new Error(`Unwanted state: ${name} not found in schema.`)
  }
  return node
}

const tableCellConverter = {
  fromPrismic(schema: Schema) {
    return function (cell: TableCellBlock): ProseMirrorNode {
      const node = getNodeFromSchema(schema, TTTableCell.name)
      return node.create(
        { colspan: 1, rowspan: 1, colwidth: null },
        cell.content.value.flatMap((data) => {
          return Paragraph.converter.fromPrismic(schema)(data) || []
        }),
      )
    }
  },

  toPrismic(node: ProseMirrorNode): TableCellBlock {
    const content: TextBlock[] = []
    node.content.forEach((node) => {
      const textBlock = Paragraph.converter.toPrismic(node)
      if (TextBlock.is(textBlock)) content.push(textBlock)
    })

    return {
      type: "tableCell",
      content: { __TYPE__: "StructuredTextContent", value: content },
    }
  },
}

const tableHeaderConverter = {
  fromPrismic(schema: Schema) {
    return function (header: TableHeaderBlock): ProseMirrorNode {
      const node = getNodeFromSchema(schema, TTTableHeader.name)
      return node.create(
        { colspan: 1, rowspan: 1, colwidth: null },
        header.content.value.flatMap((data) => {
          return Paragraph.converter.fromPrismic(schema)(data) || []
        }),
      )
    }
  },

  toPrismic(node: ProseMirrorNode): TableHeaderBlock {
    const content: TextBlock[] = []
    node.content.forEach((node) => {
      const textBlock = Paragraph.converter.toPrismic(node)
      if (TextBlock.is(textBlock)) content.push(textBlock)
    })

    return {
      type: "tableHeader",
      content: { __TYPE__: "StructuredTextContent", value: content },
    }
  },
}

const tableRowConverter = {
  fromPrismic(schema: Schema) {
    return function (row: TableRowBlock): ProseMirrorNode {
      const node = getNodeFromSchema(schema, TTTableRow.name)
      return node.create(
        {},
        row.content.map((data) => {
          switch (data.type) {
            case "tableHeader": {
              return tableHeaderConverter.fromPrismic(schema)(data)
            }
            case "tableCell": {
              return tableCellConverter.fromPrismic(schema)(data)
            }
          }
        }),
      )
    }
  },

  toPrismic(node: ProseMirrorNode): TableRowBlock {
    const content: (TableHeaderBlock | TableCellBlock)[] = []
    node.content.forEach((cell) => {
      switch (cell.type.name) {
        case "tableHeader": {
          content.push(tableHeaderConverter.toPrismic(cell))
          break
        }
        case "tableCell": {
          content.push(tableCellConverter.toPrismic(cell))
          break
        }
      }
    })

    return { type: "tableRow", content }
  },
}

const tableConverter = {
  fromPrismic(schema: Schema) {
    return function (table: TableBlock): ProseMirrorNode {
      const node = getNodeFromSchema(schema, TTTable.name)
      return node.create(
        {},
        table.content.map(tableRowConverter.fromPrismic(schema)),
      )
    }
  },

  toPrismic(node: ProseMirrorNode): TableBlock {
    return {
      type: "table",
      content: NodeUtils.children.map<TableRowBlock, false>(node, (row) =>
        tableRowConverter.toPrismic(row),
      ),
    }
  },
}

export const Table = new TableExtension(
  [ExtendedRichTextNodeType.table],
  ExtendedRichTextNodeType.table,
  [TTTable, TTTableRow, TTTableHeader, TTTableCell],
  tableConverter,
)
